import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, User, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import notificationService from '../../../services/notificationService';
import { toast } from 'react-hot-toast';

const NotificationTester = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);

  useEffect(() => {
    fetchNotifications();
    fetchBadgeCount();
    fetchEventTypes();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications();
      setNotifications(response.results || []);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchBadgeCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch badge count:', error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const types = await notificationService.getEventTypes();
      setEventTypes(types);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const markAsRead = async (notificationIds) => {
    try {
      await notificationService.markAsRead(notificationIds);
      fetchNotifications();
      fetchBadgeCount();
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      fetchNotifications();
      fetchBadgeCount();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const triggerTestNotification = async (eventType) => {
    try {
      // Create a real test notification via API
      const { default: api } = await import('../../../services/api');
      
      // For test notifications, navigate to safe fallback pages instead of fake IDs
      const testPayloads = {
        'participant.created': {
          participant_number: 'P001',
          site_code: '0001',
          // Don't include fake IDs - will fall back to no navigation for tests
        },
        'survey.missed': {
          survey_name: 'Daily Pain Assessment',
          participant_number: 'P002', 
          site_code: '0002',
          // Don't include fake IDs - will fall back to no navigation for tests
        },
        'dcq.updated': {
          survey_name: 'Protocol Deviation Review',
          // Don't include fake IDs - will fall back to no navigation for tests  
        }
      };

      await api.post('/api/dev/create-test-notification/', {
        event_type: eventType,
        payload: testPayloads[eventType] || {}
      });

      toast.success(`Test notification created: ${eventType}`);
      
      // Wait a moment then refresh
      setTimeout(() => {
        fetchNotifications();
        fetchBadgeCount();
      }, 500);
    } catch (error) {
      console.error('Failed to create test notification:', error);
      toast.error(`Failed to create test notification: ${error.response?.data?.error || error.message}`);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div>
      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
        <Bell className="mr-2" size={24} />
        Notification System Tester
        {unreadCount > 0 && (
          <span className="ml-2 bg-red-500 text-white px-2 py-1 rounded-full text-sm">
            {unreadCount} unread
          </span>
        )}
      </h3>
      
      <div className="space-y-6">
        {/* Test Trigger Buttons */}
        <div>
          <h4 className="text-lg font-semibold mb-3">Test Notification Events</h4>
          <div className="flex gap-2 flex-wrap">
            <button 
              onClick={() => triggerTestNotification('participant.created')}
              className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              <User size={14} className="inline mr-1" />
              Participant Created
            </button>
            <button 
              onClick={() => triggerTestNotification('survey.missed')}
              className="px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm"
            >
              <AlertTriangle size={14} className="inline mr-1" />
              Survey Missed
            </button>
            <button 
              onClick={() => triggerTestNotification('dcq.updated')}
              className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
            >
              <CheckCircle size={14} className="inline mr-1" />
              DCQ Updated
            </button>
            <button 
              onClick={fetchNotifications}
              className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              <RefreshCw size={14} className="inline mr-1" />
              Refresh
            </button>
          </div>
        </div>

        {/* Actions */}
        <div>
          <h4 className="text-lg font-semibold mb-3">Bulk Actions</h4>
          <div className="flex gap-2">
            <button 
              onClick={markAllAsRead}
              className="px-3 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 text-sm"
              disabled={unreadCount === 0}
            >
              Mark All Read ({unreadCount})
            </button>
          </div>
        </div>

        {/* Event Types List */}
        {eventTypes.length > 0 && (
          <div>
            <h4 className="text-lg font-semibold mb-3">Available Event Types</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {eventTypes.map(eventType => (
                <div 
                  key={eventType.key}
                  className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-900"
                >
                  <div className="font-medium text-sm">{eventType.key}</div>
                  <div className="text-xs text-gray-600 mt-1">{eventType.description}</div>
                  <div className="flex items-center mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      eventType.default_enabled 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700'
                    }`}>
                      {eventType.default_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {eventType.audience_hint && (
                      <span className="text-xs text-gray-500 ml-2">
                        Target: {eventType.audience_hint}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div>
          <h4 className="text-lg font-semibold mb-3">Recent Notifications</h4>
          {loading ? (
            <div className="text-center py-4">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell size={32} className="mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">No notifications yet</p>
              <p className="text-xs text-gray-400 mt-1">Trigger a test notification above</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {notifications.map(notification => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 border rounded ${
                    notification.is_read 
                      ? 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700' 
                      : 'bg-blue-50 border-blue-200 border-l-4 border-l-blue-400'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className={`font-semibold text-sm ${
                          notification.is_read ? 'text-gray-700' : 'text-gray-900'
                        }`}>
                          {notification.title}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          {!notification.is_read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.body}
                      </p>
                      
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            {notification.event_type_key}
                          </span>
                          
                          {notification.actor_name && notification.actor_name !== 'System' && (
                            <span className="text-xs text-gray-500">
                              by {notification.actor_name}
                            </span>
                          )}
                        </div>
                        
                        {!notification.is_read && (
                          <button
                            onClick={() => markAsRead([notification.id])}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Mark Read
                          </button>
                        )}
                      </div>

                      {/* Context badges */}
                      {(notification.payload?.site_code || notification.payload?.participant_number) && (
                        <div className="flex items-center space-x-2 mt-2">
                          {notification.payload?.site_code && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700">
                              Site {notification.payload.site_code}
                            </span>
                          )}
                          {notification.payload?.participant_number && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                              P{notification.payload.participant_number}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationTester;
